import React, { useEffect, useRef } from 'react';
import { Itinerary } from '../../types';
import './DeleteDialog.css';

interface DeleteDialogProps {
  itinerary: Itinerary;
  onClose: () => void;
  onDelete: (deleteEvents: boolean) => void;
}

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  itinerary,
  onClose,
  onDelete,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    triggerRef.current = document.activeElement;
    // Undoable-action default (Almanac S9a): the way out holds the initial
    // focus, so Enter or Space keeps the plan; the container is the fallback.
    (cancelRef.current ?? modalRef.current)?.focus();
    return () => {
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="dialog-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Delete itinerary">
      <div className="dialog delete-dialog" ref={modalRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <h3>Delete Itinerary</h3>
        <p>Are you sure you want to delete "{itinerary.title}"?</p>
        {itinerary.is_published && (
          <p className="dialog-warning">
            This itinerary has calendar events. Do you want to delete them too?
          </p>
        )}
        <div className="dialog-actions">
          <button ref={cancelRef} onClick={onClose} className="btn-cancel">
            Cancel
          </button>
          {itinerary.is_published ? (
            <>
              <button onClick={() => onDelete(false)} className="btn-secondary">
                Keep Events
              </button>
              <button onClick={() => onDelete(true)} className="btn-danger">
                Delete All
              </button>
            </>
          ) : (
            <button onClick={() => onDelete(true)} className="btn-danger">
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteDialog;
